import _ from 'lodash';
import {
  ReactNode,
  useMemo,
  useState
} from 'react';

import {
  Bar,
  BarChart,
  BarProps,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import { CustomToolTip } from 'modules';
import { theme } from 'theme';



export type BarChartBaseProps = {
  data: {
    name: string;
    rate: string;
  }[];
  toolTip?: ReactNode;
  minY?: number;
  maxY?: number;
  hideYAxis?: boolean;

  yAxisFormatter?: (val: number) => string;
};

export const FC = (props: BarChartBaseProps) => {
  const [
    activeIndex,
    setActiveIndex
  ] = useState<string | undefined>(undefined);

  const [
    barGraphData,
    setBarGraphData
  ] = useState<any>({
  });

  const { data } = props;

  const handleClick = (index: BarProps) => {
    setBarGraphData(() => index);
    setActiveIndex(() => index.name?.toString());
  };

  const onMouseLeave = (index: BarProps) => {
    setBarGraphData(() => undefined);
    setActiveIndex(() => undefined);
  };

  const mappedData = data.map((datum) => ({
    name: datum.name,
    rate: _.toNumber(datum.rate)
  }));

  const ticks = useMemo(() => {
    if (!_.isNil(props.minY) && !_.isNil(props.maxY)) {
      const max = __makeTicks(props.maxY, 3);
      const min = __makeTicks(props.minY, 3);

      const uniques = _.sortBy(_.uniq([
        ...max,
        ...min
      ])).reverse();
      return uniques;
    }

    return undefined;
  }, [
    props.maxY,
    props.minY
  ]);



  return (
    <ResponsiveContainer
      width='100%'
      height='100%'
    >
      <BarChart
        width={400}
        height={300}
        margin={{
          right: 5,
          bottom: 5
        }}
        data={mappedData}
        stackOffset='sign'
      >
        <Bar
          onMouseEnter={handleClick}
          onMouseLeave={onMouseLeave}
          dataKey='rate'
          background={{
            fill: 'rgba(103, 114, 109, 0.10)'
          }}
        >

          {data.map((entry, index) => (
            <Cell
              radius={15}
              key={index}
              fill={`${entry.name === activeIndex
                ? _.toNumber(entry.rate) >= 0
                  ? theme.palette.primary.darker200
                  : theme.palette.error.light80
                : _.toNumber(entry.rate) >= 0
                  ? theme.palette.primary.main
                  : theme.palette.error.main}`}
            />
          ))}
        </Bar>
        {props.toolTip
          ? props.toolTip
          : (
            <Tooltip
              position={{
                y: barGraphData.y + 50
              }}
              cursor={{
                fill: 'transparent'
              }}
              offset={0}
              content={(
                <CustomToolTip.FC
                  type='BAR'
                  active={true}
                  label='name'
                  payload={data}
                />
              )}
            />
          )}

        <XAxis
          fontFamily='Lexend'
          dataKey='name'
        />

        <YAxis
          axisLine={!props.hideYAxis}
          tick={!props.hideYAxis}
          interval='preserveStartEnd'
          fontFamily='Lexend'
          ticks={ticks}
          type='number'
          tickFormatter={(v: any) => {
            if (_.isNumber(v)) {
              if (props.yAxisFormatter) {
                return props.yAxisFormatter(v);
              }
              return v.toFixed(2);
            }
            return v;
          }}

          domain={(!_.isNil(props.minY) && !_.isNil(props.maxY))
            ? [
              props.minY === props.maxY ? 0 : props.minY,
              props.maxY
            ]
            : [
              _.minBy(mappedData, (datum) => datum.rate)?.rate ?? 0,
              _.maxBy(mappedData, (datum) => datum.rate)?.rate ?? 0
            ]}
        />
      </BarChart>
    </ResponsiveContainer>

  );
};



function __makeTicks(maxValue: number, count: number) {
  const interval = maxValue / count;

  const intervals = _.times(count, (i) => interval * (i + 1)).map((v) => _.round(v, 2));

  return [
    0,
    ...intervals
  ];
}
